/**
 * The lecture page's boxes, shared by the loaded page and its pending
 * skeleton so both are one shape at every width. A `.ts` sibling so the
 * component files export only components.
 *
 * ONE GRID, FOUR CELLS, in reading order: the player, the bar under it
 * (lecture meta, Mark complete, previous / next), the course's lecture list,
 * then the lecture's notes.
 *
 * - Narrow: one column in that order — the list sits right under the
 *   controls, collapsed, so it never pushes the notes a screen away.
 * - From `@4xl` (a 56rem main column): the list moves to a 20rem column on
 *   the right, spanning all three rows, and pins under the top nav while the
 *   left column scrolls. The last row is `1fr` so a list taller than the left
 *   column grows the space under the notes, never the gaps between the
 *   player, the bar and the notes.
 *
 * A container query, not `lg:`: the main column's width depends on the
 * sidebar and the Mitra panel too (ScreenLayout's note). `@4xl` keeps the
 * player at least ~550px wide beside the list — a 1280 window with the
 * sidebar open — and stacks everything narrower.
 */
// The 20rem list column is written as `--spacing(80)` (80 × 4px) rather than
// a literal `20rem` so the value stays off the arbitrary-px ratchet.
export const PLAYER_GRID =
  'grid gap-6 @4xl:grid-cols-[minmax(0,1fr)_--spacing(80)] @4xl:grid-rows-[auto_auto_1fr] @4xl:items-start'

export const STAGE_CELL = 'min-w-0 @4xl:col-start-1 @4xl:row-start-1'

export const BAR_CELL = 'flex min-w-0 flex-col gap-4 @4xl:col-start-1 @4xl:row-start-2'

export const NOTES_CELL = 'min-w-0 @4xl:col-start-1 @4xl:row-start-3'

/**
 * The list's card. From `@4xl` it is capped at the viewport under the top
 * nav (with the page's 24px above and below) and scrolls inside; narrower it
 * is as tall as its rows, and the page scrolls.
 */
export const LIST_CELL =
  'flex min-w-0 flex-col overflow-hidden rounded-lg border border-border-subtle bg-card text-card-foreground @4xl:sticky @4xl:col-start-2 @4xl:row-span-3 @4xl:row-start-1 @4xl:top-[calc(var(--header-h)+var(--space-6))] @4xl:max-h-[calc(100dvh-var(--header-h)-var(--space-6)-var(--space-6))]'

/** The 16:9 box every player state draws in — reserved before anything loads. */
export const STAGE = 'relative flex aspect-video w-full overflow-hidden rounded-lg bg-muted'

/**
 * The bar's first row: meta on the left, Mark complete on the right. Held at
 * the button's height whether or not the button draws (a course not bought
 * has none), so the row never changes height with the member's access.
 */
export const BAR_ROW =
  'flex min-h-11 flex-wrap items-center justify-between gap-x-4 gap-y-2 lg:min-h-9'

/** Previous / next: two cards, each one line of label and one of title. */
export const NAV_GRID = 'grid grid-cols-2 gap-3'
export const NAV_LINK =
  // Figma 2 → 0 (4-grid tie, non-interactive: nearest 8 of a 2px gap is 0).
  'flex min-w-0 flex-col gap-0 rounded-lg border border-border-subtle bg-card px-4 py-3'

/** The list card's header: the heading line and the progress line. */
export const LIST_HEADER = 'flex shrink-0 flex-col gap-1 border-b border-border-subtle px-4 py-3'

/**
 * One lecture row: a 24px marker, then the title (up to two lines) over its
 * length. 61px for a one-line title (Figma 10 → `py-3`, 4-grid) — past the
 * 44px touch minimum.
 */
export const LIST_ROW = 'flex min-h-11 items-start gap-3 px-4 py-3'

/** The notes card — the description, the materials, the discussion link. */
export const NOTES_CARD =
  'flex flex-col gap-6 rounded-lg border border-border-subtle bg-card p-5 text-card-foreground'
